/**
 * PaymentMethodPage — COMMIT-4: Kart guncelleme (Stripe SETUP mode, embedded on-page).
 *
 * Akis: POST /api/payments/create-checkout {package_id:'__payment_method__', ui_mode:'embedded'}
 *        -> client_secret -> <EmbeddedCheckoutProvider><EmbeddedCheckout/></> (opara.co'da kalir; redirect YOK).
 * Backend setup dali PaymentMethod'u subscription + customer default'a yazar, acik faturayi tahsil eder.
 * Bu sayfa yalniz skin+entegrasyon; CheckoutPage'in embedded deseninin BIREBIR ayni yapisi.
 * i18n: yeni anahtar ACILMADI - mevcut 'userstatus.update_card' kullanildi (COMMIT-5'te ozel metinler).
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

import { API } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getSettingsPath } from "@/utils/localePaths";
import { getStripePromise } from "@/utils/stripeClient";

const GOLD = "#F5C518";

export default function PaymentMethodPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { t, locale } = useTranslation();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stripePromise] = useState(() => getStripePromise());

  const loginPath = getLoginPath(locale);
  const settingsPath = getSettingsPath(locale);

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate(loginPath);
  }, [user, navigate, loginPath, authLoading]);

  const startSetup = async () => {
    if (!token) return;
    setStarting(true);
    try {
      const res = await axios.post(
        `${API}/payments/create-checkout`,
        {
          package_id: "__payment_method__",
          origin_url: window.location.origin,
          locale: locale || "en",
          ui_mode: "embedded",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.client_secret) {
        setClientSecret(res.data.client_secret);
      } else {
        toast.error("Could not start card update.");
      }
    } catch (error) {
      const status = error.response?.status;
      const _d = error.response?.data?.detail;
      const _msg = (_d && typeof _d === "object") ? (_d.message || null) : (_d || null);
      if (status === 400) toast.error(_msg || "Invalid request.");
      else if (status === 503) toast.error("Payment system is warming up. Please try again in a moment.");
      else toast.error(_msg || "Could not start card update.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: "#0B0B0D" }}>
      <header className="border-b border-gray-800/80">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/en/")}>
            <img src="/opara-logo-trimmed.png" alt="OPARA" className="h-8 w-auto object-contain" />
          </div>
          <button onClick={() => navigate(settingsPath)} aria-label="Back" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 lg:py-12">
        <h1 className="text-xl font-bold mb-1">{t("paymentmethod.title")}</h1>
        <p className="text-gray-400 text-sm mb-6">{t("paymentmethod.subtitle")}</p>

        {!clientSecret ? (
          <div className="rounded-2xl border border-gray-800 bg-[#141416] p-6 lg:p-8">
            <Button onClick={startSetup} disabled={starting} className="w-full h-12 text-black font-bold text-base" style={{ backgroundColor: GOLD }}>
              {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Lock className="w-5 h-5 mr-2" />}
              {t("paymentmethod.subtitle")}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-800 bg-white p-2 sm:p-4 overflow-hidden">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-[12px] text-gray-500">
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" style={{ color: GOLD }} /> Secured by Stripe</span>
          <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" style={{ color: GOLD }} /> 256-bit SSL</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} /> PCI-DSS compliant</span>
        </div>
      </main>
    </div>
  );
}
